import { FC, useEffect, useRef, useState } from 'react'
import styles from './footer.module.scss'
import Logotype from '@/components/ui/Logotype'
import FooterNav from './FooterNav'
import FooterBottom from './FooterBottom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectedThemeMode } from '@/redux/theme/selectors'
import { useRouter } from 'next/router'

type GSAPModules = {
  gsap: typeof import('gsap').default
  ScrollTrigger: typeof import('gsap/dist/ScrollTrigger').default
}

type FooterProps = {}

const Footer: FC<FooterProps> = ({}) => {
  const { t } = useTranslation()
  
  const router = useRouter()
  const themeMode = useSelector(selectedThemeMode)
  
  const innerRef = useRef<HTMLDivElement | null>(null)
  const scopeRef = useRef<HTMLDivElement | null>(null)
  const [gsapModules, setGsapModules] = useState<GSAPModules | null>(null)

  useEffect(() => {
    const loadGsap = async () => {
      try {
        const [{ default: gsap }, { useGSAP }, { default: ScrollTrigger }] = await Promise.all([
          import('gsap'),
          import('@gsap/react'),
          import('gsap/dist/ScrollTrigger'),
        ])

        gsap.registerPlugin(useGSAP)
        gsap.registerPlugin(ScrollTrigger)

        setGsapModules({ gsap, ScrollTrigger })
      } catch (error) {
        console.error('Failed to load GSAP:', error)
      }
    }

    loadGsap()
  }, [])

  useEffect(() => {
    if (!gsapModules || !innerRef.current) return

    const { gsap, ScrollTrigger } = gsapModules

    const maxValue = window.innerWidth > 834 ? 70 : 50
    const minValue = window.innerWidth > 834 ? 55 : 35

    gsap.to(innerRef.current, {
      marginTop: `calc(-1 * max(${maxValue / 1440 * window.innerWidth}px, ${minValue}px))`,
      scrollTrigger: {
        trigger: innerRef.current,
        end: '75% 100%',
        scrub: 0.1,      
      },
    })

    requestAnimationFrame(() => {
      try {
        ScrollTrigger.refresh()
      } catch {}
    })

    return () => {
      ScrollTrigger.killAll()
    }
  }, [gsapModules, themeMode, router.asPath])

  return (
    <footer ref={scopeRef} className={styles.footer}>
      <div className="container">
        <Logotype className={styles.footer__logo} />
        <div ref={innerRef} className={styles.inner}>
          <FooterNav />
          <FooterBottom />
        </div>
      </div>
    </footer>
  )
}

export default Footer
